import { useCallback, useEffect, useMemo, useState } from 'react'

import type { FilterOption } from '../components/filter/Filter'
import {
  flattenFilterOptions,
  isFilterOptionSelectable,
} from '../utils/flattenFilterOptions'

interface IUseFilterKeyboardNavArgs<V> {
  options: Array<FilterOption<V>>
  expanded: Set<unknown>
  isOpen: boolean
  onToggle: (option: FilterOption<V>) => void
  onToggleExpand: (value: V) => void
  onCommit: () => void
  onCancel: () => void
  rowRefs: Map<unknown, HTMLElement>
}

interface IUseFilterKeyboardNavResult<V> {
  activeValue: V | null
  setActiveValue: (value: V | null) => void
  handleKeyDown: (event: React.KeyboardEvent) => void
}

export const useFilterKeyboardNav = <V>({
  options,
  expanded,
  isOpen,
  onToggle,
  onToggleExpand,
  onCommit,
  onCancel,
  rowRefs,
}: IUseFilterKeyboardNavArgs<V>): IUseFilterKeyboardNavResult<V> => {
  const [activeValue, setActiveValue] = useState<V | null>(null)

  const visibleFlat = useMemo(() => {
    const all = flattenFilterOptions(options)
    return all.filter((entry) => {
      if (entry.depth === 0) return true
      return entry.parentValues.every((parent) => expanded.has(parent))
    })
  }, [options, expanded])

  useEffect(() => {
    if (!isOpen) {
      setActiveValue(null)
    }
  }, [isOpen])

  const moveActive = useCallback(
    (delta: number) => {
      if (visibleFlat.length === 0) return
      const navigable = visibleFlat.filter(
        (entry) =>
          isFilterOptionSelectable(entry.option) ||
          (entry.isParent && entry.option.selectable !== false)
      )
      if (navigable.length === 0) return
      const currentIndex = navigable.findIndex(
        (entry) => entry.option.value === activeValue
      )
      let nextIndex = currentIndex + delta
      if (currentIndex === -1) {
        nextIndex = delta > 0 ? 0 : navigable.length - 1
      } else {
        if (nextIndex < 0) nextIndex = navigable.length - 1
        if (nextIndex >= navigable.length) nextIndex = 0
      }
      const nextValue = navigable[nextIndex].option.value
      setActiveValue(nextValue)
      const el = rowRefs.get(nextValue)
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest' })
      }
    },
    [visibleFlat, activeValue, rowRefs]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen) return
      const active = visibleFlat.find(
        (entry) => entry.option.value === activeValue
      )
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          moveActive(1)
          break
        case 'ArrowUp':
          event.preventDefault()
          moveActive(-1)
          break
        case 'ArrowRight':
          if (active?.isParent && !expanded.has(activeValue!)) {
            event.preventDefault()
            onToggleExpand(activeValue as V)
          }
          break
        case 'ArrowLeft':
          if (active?.isParent && expanded.has(activeValue!)) {
            event.preventDefault()
            onToggleExpand(activeValue as V)
          }
          break
        case 'Home':
          event.preventDefault()
          if (visibleFlat[0]) setActiveValue(visibleFlat[0].option.value)
          break
        case 'End':
          event.preventDefault()
          if (visibleFlat.length > 0) {
            setActiveValue(visibleFlat[visibleFlat.length - 1].option.value)
          }
          break
        case ' ':
        case 'Enter':
          if (active) {
            event.preventDefault()
            onToggle(active.option)
          } else if (event.key === 'Enter') {
            event.preventDefault()
            onCommit()
          }
          break
        case 'Escape':
          event.preventDefault()
          onCancel()
          break
        default:
          break
      }
    },
    [
      isOpen,
      visibleFlat,
      activeValue,
      moveActive,
      expanded,
      onToggleExpand,
      onToggle,
      onCommit,
      onCancel,
    ]
  )

  return { activeValue, setActiveValue, handleKeyDown }
}
